import { SavingRegion } from '@common/components/SavingRegion';
import { useGuestFieldErrors } from '@common/hooks/useGuestFieldErrors';
import { getDetailRoute } from '@features/rates/helpers/bookingRoutes.helper';
import { useRoomPageSettings } from '@features/rooms';
import { useTranslations } from '@lang';
import { buildExistingGuests } from '@utils/buildExistingGuests';
import { femaleTitlesOptions, maleTitlesOptions } from '@utils/guestTitles';
import rewriteHttp from '@utils/rewriteHttp';
import { scrollToFirstError } from '@utils/scrollToFirstError';
import { useExistingGuestSelect } from '@utils/useExistingGuestSelect';
import { useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import type { UseFormReturn } from 'react-hook-form';
import { useRouter } from 'shared-routing';
import { GuestDetailsTemplate } from 'voyager';
import type { GuestDetailsGuestEntry, GuestFormValues } from 'voyager';
import { RoomRateFormValues } from '../../RoomRateForm.schema';
import { findSelectedEntity, toPayableStayPrice } from '../selectedEntity.helper';
import { GuestDetailsSelectedEntity, GuestDetailsStepV2Props } from './GuestDetailsStepV2.types';

type GuestFormEntry = RoomRateFormValues['guests'][number];

const emptyGuest: GuestFormEntry = { title: '', firstName: '', lastName: '', gender: '' };

const hasGuestValues = (guest?: GuestFormEntry) =>
  !!guest && !!(guest.firstName || guest.lastName || guest.title);

const getInitialGuestCount = (guests: GuestFormEntry[]) =>
  guests.reduce(
    (count, guest, index) => (index > 0 && hasGuestValues(guest) ? index + 1 : count),
    1
  );

export const GuestDetailsStepV2 = ({
  metaEntity,
  entity,
  basketGuests,
  loggedGuest,
  availableExtras,
  addToBasket,
  goToNextStep,
  goToPreviousStep,
  isFetching,
  maxGuests,
  language,
}: GuestDetailsStepV2Props) => {
  const { t } = useTranslations();
  const router = useRouter();
  const form = useFormContext<RoomRateFormValues>();
  const {
    handleSubmit,
    watch,
    setValue,
    formState: { isSubmitting, isSubmitted, errors },
  } = form;

  const selectedEntity = findSelectedEntity(metaEntity, entity.id) as
    | GuestDetailsSelectedEntity
    | undefined;

  useEffect(() => {
    if (selectedEntity) {
      return;
    }
    router.push({
      pathname: getDetailRoute(router.pathname, metaEntity.type),
      query: { id: metaEntity.id },
    });
  }, [selectedEntity, router, metaEntity.type, metaEntity.id]);

  const guestValues = watch('guests') ?? [];
  const noteValue = watch('note') ?? '';

  const [guestCount, setGuestCount] = useState(() => getInitialGuestCount(guestValues));
  const [notesChecked, setNotesChecked] = useState(() => !!noteValue);

  const { content: roomsContent } = useRoomPageSettings(language, t('common.rate'));

  const existingGuests = useMemo(
    () => buildExistingGuests(basketGuests ?? [], loggedGuest),
    [basketGuests, loggedGuest]
  );

  const onExistingGuestSelect = useExistingGuestSelect({
    form: form as UseFormReturn<any>,
    existingGuests,
    setValueOptions: { shouldValidate: true },
  });

  const displayedCount = Math.max(Math.min(guestCount, maxGuests), 1);
  const displayedGuests = Array.from(
    { length: displayedCount },
    (_, index) => guestValues[index] ?? emptyGuest
  );

  const translateGuestErrors = useGuestFieldErrors();

  const guests: GuestDetailsGuestEntry[] = displayedGuests.map((guest, index) => ({
    errors: isSubmitted ? translateGuestErrors(errors.guests?.[index]) : undefined,
    values: {
      title: guest.title ?? '',
      firstName: guest.firstName ?? '',
      lastName: guest.lastName ?? '',
      gender: guest.gender ?? '',
    },
    selectedExistingGuestId: guest.selectedGuestId,
  }));

  const handleGuestChange = (guestIndex: number, field: keyof GuestFormValues, value: string) => {
    const current = form.getValues('guests') ?? [];
    const updated = [...current];
    if (!updated[guestIndex]) {
      updated[guestIndex] = { ...emptyGuest };
    }
    updated[guestIndex] = { ...updated[guestIndex], [field]: value };
    setValue('guests', updated, { shouldValidate: true });
  };

  const handleAddGuest = () => {
    const nextCount = Math.min(displayedCount + 1, maxGuests);
    const current = form.getValues('guests') ?? [];
    if (current.length < nextCount) {
      setValue(
        'guests',
        [
          ...current,
          ...Array.from({ length: nextCount - current.length }, () => ({ ...emptyGuest })),
        ],
        { shouldValidate: true }
      );
    }
    setGuestCount(nextCount);
  };

  const handleRemoveGuest = (guestIndex: number) => {
    if (guestIndex === 0) {
      return;
    }
    const current = form.getValues('guests') ?? [];
    const updated = Array.from(
      { length: Math.max(current.length, displayedCount) },
      (_, index) => current[index] ?? { ...emptyGuest }
    );
    for (let index = guestIndex; index < displayedCount - 1; index += 1) {
      updated[index] = { ...updated[index + 1] };
    }
    updated[displayedCount - 1] = { ...emptyGuest };
    setValue('guests', updated, { shouldValidate: true });
    setGuestCount(Math.max(displayedCount - 1, 1));
  };

  const handleNotesToggle = (checked: boolean) => {
    setNotesChecked(checked);
    if (!checked) {
      setValue('note', '');
    }
  };

  const areExtrasLoading = availableExtras === null;
  const hasExtras = !!availableExtras?.length;
  const totalSteps = areExtrasLoading ? undefined : hasExtras ? 2 : 1;

  const onSubmit = async (values: RoomRateFormValues) => {
    if (areExtrasLoading || hasExtras) {
      goToNextStep();
    } else {
      await addToBasket({ guestDetails: values.guests, note: values.note });
      goToNextStep();
    }
  };

  const imageSource =
    selectedEntity?.imageUrl ??
    selectedEntity?.rateImageUrl ??
    metaEntity.imageUrl ??
    roomsContent?.heroBackgroundImage ??
    undefined;
  const footerTitle = selectedEntity?.name ?? selectedEntity?.rateTitle ?? metaEntity.title ?? '';
  const totalAmount = toPayableStayPrice(selectedEntity)?.amount ?? 0;
  const currencyCode = selectedEntity?.pricePerStay?.currencyCode ?? 'GBP';

  const titleOptions = useMemo(
    () =>
      [...maleTitlesOptions, ...femaleTitlesOptions].map((option) => ({
        value: option.value,
        label: t(option.label),
      })),
    [t]
  );

  const labels = useMemo(
    () => ({
      title: t('room.guestDetails.title'),
      titleAccent: t('room.guestDetails.subtitle'),
      backAriaLabel: t('common.back'),
      progressLabel: t('aria.bookingProgress'),
      progressValueText: t('aria.bookingStep', { current: 1, total: totalSteps }),
      guestTitleFormatter: (guestNumber: number) =>
        t('room.guestDetails.guestTitle', { number: guestNumber }),
      guestSubtitle: t('spa.booking.guestDetails.subtitle'),
      dividerText: t('spa.booking.guestDetails.divider'),
      titleLabel: t('common.title'),
      titlePlaceholder: t('spa.booking.guestDetails.selectPlaceholder'),
      firstNameLabel: t('common.firstName'),
      lastNameLabel: t('common.lastName'),
      addGuest: t('common.addMoreGuests'),
      removeGuest: t('common.remove'),
      notesTitle: t('room.guestDetails.notesTitle'),
      notesPlaceholder: t('room.guestDetails.notesPlaceholder'),
      totalLabel: t('basket.total'),
      nextLabel: t('common.next'),
    }),
    [t, totalSteps]
  );

  return selectedEntity ? (
    <SavingRegion isSaving={isFetching}>
      <GuestDetailsTemplate
        imageUrl={imageSource ? rewriteHttp(imageSource) : undefined}
        imageAlt={footerTitle}
        guests={guests}
        onGuestChange={handleGuestChange}
        onExistingGuestSelect={onExistingGuestSelect}
        existingGuests={existingGuests.length ? existingGuests : undefined}
        titleOptions={titleOptions}
        maxGuests={maxGuests}
        onAddGuest={handleAddGuest}
        onRemoveGuest={handleRemoveGuest}
        notesChecked={notesChecked}
        onNotesToggle={handleNotesToggle}
        notesValue={noteValue}
        onNotesChange={(value) => setValue('note', value)}
        onBack={goToPreviousStep}
        currentStep={1}
        totalSteps={totalSteps}
        footerTitle={footerTitle}
        totalAmount={totalAmount}
        currencyCode={currencyCode}
        locale={language}
        onNext={handleSubmit(onSubmit, scrollToFirstError)}
        nextDisabled={isSubmitting || isFetching}
        labels={labels}
      />
    </SavingRegion>
  ) : null;
};
